import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck, Truck, Wrench, Star, ArrowRight, Phone, Mail, MapPin, ExternalLink, Play, Pause, CheckCircle2, Sparkles, Quote, Zap, Layers, Building2 } from 'lucide-react';
import { COMPANY_INFO, POPULAR_CATEGORIES, TESTIMONIALS, STAFF } from '../data';
import ContactForm from '../components/ContactForm';

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play().catch((err) => console.log("Video play failed:", err));
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/pages')
      .then((res) => {
        if (!res.ok) throw new Error('Kunne ikke hente forside-data');
        return res.json();
      })
      .then((data) => {
        const found = data.find((p) => p.tech_slug === 'index');
        if (found) {
          setPageData(found);
        }
      })
      .catch((err) => {
        console.warn('CMS forside-data fejlede, bruger fallback:', err);
      });
  }, []);

  // Cloudflare on-the-fly billedoptimering
  const getOptimizedUrl = (url, width = 800) => {
    if (url && url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace(
        'https://assets.auxodev.dk/',
        `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`
      );
    }
    return url;
  };

  const metaTitle = pageData?.meta_title || "Vesthimmerlands Materieludlejning | Lej Maskiner";
  const metaDesc = pageData?.meta_description || "Lej lifte, minikraner, minilæssere og entreprenørmaskiner hos Vesthimmerlands Materieludlejning i Svenstrup. Hurtig levering og god service.";

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta name="google-site-verification" content="z3tpFosgSFOVT5t7YxrR5dRUus2G0My4mv4wO9KNEf8" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/vesthimmerlands-materieludlejning-plads-maskiner.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/vesthimmerlands-materieludlejning-plads-maskiner.jpg" />
        <link rel="canonical" href="https://vhmu.dk/" />
      </Helmet>

      {/* Hero Sektion: Lys, indbydende og professionel */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-gradient-to-b from-white via-gray-50 to-white py-12 sm:py-16 lg:py-24">
        {/* CSS-animationer til det rullende billedgitter */}
        <style>{`
          @keyframes scroll-up {
            0% { transform: translateY(0); }
            100% { transform: translateY(-50%); }
          }
          @keyframes scroll-down {
            0% { transform: translateY(-50%); }
            100% { transform: translateY(0); }
          }
          .animate-scroll-up {
            animation: scroll-up 45s linear infinite;
          }
          .animate-scroll-down {
            animation: scroll-down 50s linear infinite;
          }
          .animate-scroll-up:hover, .animate-scroll-down:hover {
            animation-play-state: paused;
          }
        `}</style>

        {/* Video baggrund med gradient overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="h-full w-full object-cover opacity-40"
            title="Baggrundsvideo med entreprenørmaskiner og materiel"
            aria-label="Baggrundsvideo af entreprenørmateriel i funktion"
          >
            <source src="https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/materiel-arbejde-hero-baggrund.mp4" type="video/mp4" />
          </video>
          {/* Gradient overlay for optimal læsbarhed */}
          <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/40 to-white" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/80 via-transparent to-white/10 hidden lg:block" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 w-full">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-center">
            
            {/* Venstre Kolonne: Tekst og CTA */}
            <div className="lg:col-span-6 flex flex-col items-start text-left">
              <span className="inline-block text-xs font-black uppercase tracking-[0.25em] text-brand-yellow mb-6">
                Din foretrukne "lejekammerat"
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-black uppercase leading-tight sm:leading-[1.1] tracking-tight text-gray-900 break-words">
                <span className="block mb-1">Vesthimmerlands</span>
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className="text-brand-yellow">
                    Materieludlejning
                  </span>
                </span>
              </h1>
              <p className="mt-6 max-w-xl text-base text-gray-600 md:text-lg leading-relaxed">
                Landsdækkende service til fornuftige priser. Vi udlejer moderne, godkendte og driftssikre maskiner, <Link to="/liftudlejning" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">lifte</Link>, <Link to="/minikran" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minikraner</Link> og <Link to="/minilaesser" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minilæssere</Link> til dit næste projekt.
              </p>

              {/* CTA Knapper */}
              <div className="mt-10 flex flex-wrap gap-4 w-full sm:w-auto">
                <a
                  href="https://vesthimmerlands.brukerweb.no/product/index/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-yellow/10 px-8 py-4 text-xs font-black uppercase tracking-[0.2em] text-brand-yellow hover:bg-brand-yellow/20 transition-all duration-300 cursor-pointer shadow-sm"
                >
                  <span>Produktkatalog</span>
                  <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-8 py-4 text-xs font-black uppercase tracking-[0.2em] text-gray-800 hover:bg-gray-50 hover:border-brand-yellow transition-all duration-300"
                >
                  Få et tilbud
                </Link>
              </div>
            </div>

            {/* Højre Kolonne: Asymmetrisk, animeret billed-masonry */}
            <div className="lg:col-span-6 w-full relative">
            </div>

          </div>



        </div>
      </section>

      {/* NY SEKTION: Vores Produkter (Halvstore billeder som man kan trykke på) */}
      <section className="relative bg-brand-light py-24 md:py-32 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Vores maskiner & materiel
            </span>
            <h2 className="mt-2 text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Vores <span className="text-brand-yellow">Produktkatalog</span>
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-base text-gray-600 leading-relaxed">
              Klik på en af kategorierne nedenfor for at se vores store udvalg af maskiner og materiel direkte i vores online-katalog.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {POPULAR_CATEGORIES.map((category, idx) => (
              <div
                key={idx}
                className="group flex flex-col overflow-hidden rounded-xl border border-brand-grey bg-white transition-all duration-300 hover:border-brand-yellow/40 hover:shadow-xl"
              >
                {/* Halvstort billede - Linker direkte til eksternt katalog */}
                <a
                  href={category.catalogLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="relative aspect-[4/3] overflow-hidden bg-gray-100 block cursor-pointer"
                >
                  <img
                    src={getOptimizedUrl(category.image, 600)}
                    alt={category.alt || `${category.title} til udlejning hos Vesthimmerlands Materieludlejning`}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                  
                  {/* Eksternt ikon overlay */}
                  <div className="absolute top-3 right-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-800 shadow-md transition-transform duration-300 group-hover:scale-110">
                    <ExternalLink className="h-4 w-4 text-brand-yellow" />
                  </div>
                </a>
                
                {/* Tekstindhold */}
                <div className="p-5 flex-grow flex flex-col justify-between">
                  <div>
                    <a
                      href={category.catalogLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group-hover:text-brand-yellow transition-colors duration-200"
                    >
                      <h3 className="text-base font-black text-gray-900 uppercase tracking-wider">
                        {category.title}
                      </h3>
                    </a>
                    <p className="mt-2 text-xs text-gray-600 leading-relaxed">
                      {category.description}
                    </p>
                  </div>
                  
                  {/* Links i bunden af kortet */}
                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <Link
                      to={`/${category.slug}`}
                      className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-brand-yellow transition-colors duration-200"
                    >
                      <span>Info & Galleri</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    
                    <a
                      href={category.catalogLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-brand-yellow hover:text-brand-yellow-hover transition-colors duration-200"
                    >
                      <span>Se udvalg</span>
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  </div>
                </div>
              </div>
            ))}

            {/* 8. kasse: Se hele vores udvalg */}
            <div className="group flex flex-col justify-between overflow-hidden rounded-xl border-2 border-dashed border-brand-yellow/40 bg-white p-6 transition-all duration-300 hover:border-brand-yellow hover:shadow-xl">
              <div className="flex-grow flex flex-col justify-center text-center py-8 px-2">
                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-brand-yellow/10 text-brand-yellow transition-transform duration-300 group-hover:scale-110">
                  <ExternalLink className="h-6 w-6" />
                </div>
                <h3 className="text-base font-black text-gray-900 uppercase tracking-wider">
                  Se hele vores udvalg
                </h3>
                <p className="mt-3 text-xs text-gray-600 leading-relaxed">
                  Finder du ikke det du søger? Gå på opdagelse i vores komplette online-katalog med alt vores materiel og maskiner.
                </p>
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-100">
                <a
                  href="https://vesthimmerlands.brukerweb.no/product/index/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex w-full items-center justify-center gap-2 rounded-lg bg-brand-yellow px-4 py-3 text-xs font-black uppercase tracking-widest text-white transition-all duration-200 hover:bg-brand-yellow/90 shadow-sm hover:shadow"
                >
                  <span>Åben katalog</span>
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREMHÆVET SEKTION: Nyeste Storinvestering - Jekko Minikraner */}
      <section className="relative bg-white py-16 md:py-24 border-t border-gray-200 overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          
          {/* Sektion Overskrift & Indledning */}
          <div className="max-w-3xl mb-12 lg:mb-16 text-left">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-gray-900 leading-tight">
              Tre nye <span className="text-brand-yellow">Jekko minikraner</span><br />på vej til Vesthimmerlands Materieludlejning
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 items-stretch">
            
            {/* Venstre side: Billede & Citat */}
            <div className="lg:col-span-5 flex flex-col justify-start gap-6">
              <div className="w-full rounded-2xl overflow-hidden shadow-md border border-gray-200 bg-gray-50">
                <img
                  src={getOptimizedUrl('https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/henrik-frederiksen-jekko-minikraner-dansk-liftmesse.jpg', 1600)}
                  alt="Henrik Frederiksen på Dansk Liftmesse 2026 foran begge Jekko minikraner"
                  className="w-full h-auto object-contain block"
                />
              </div>

              {/* Citat Boks fra Henrik Frederiksen - placeret under billedet */}
              <div className="shrink-0 border-l-4 border-brand-yellow pl-5 py-1 space-y-3">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Ifølge indehaver <strong>Henrik Frederiksen</strong> er valget af Jekko enkelt:
                </p>
                <blockquote className="text-gray-900 text-base sm:text-lg italic font-medium leading-relaxed">
                  "Jekko er de bedste minikraner på markedet. De har et stærkt løftediagram, stor fleksibilitet og løser mange af de opgaver, vores kunder står med i hverdagen."
                </blockquote>
                <div className="flex items-center gap-3 pt-1">
                  <img
                    src={getOptimizedUrl('https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/henrik-frederiksen-indehaver.jpg', 150)}
                    alt="Henrik Frederiksen"
                    className="h-10 w-10 rounded-full object-cover border-2 border-brand-yellow shadow-sm"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 uppercase text-xs tracking-wider">Henrik Frederiksen</h4>
                    <p className="text-xs text-gray-500">Indehaver, Vesthimmerlands Materieludlejning</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Højre side: Historie, Ordredetaljer & Anvendelsesområder */}
            <div className="lg:col-span-7 space-y-8 text-gray-700 leading-relaxed">
              
              <p className="text-base sm:text-lg font-medium text-gray-900 leading-relaxed">
                På Dansk Liftmesse 2026 blev der sat punktum for en rigtig god handel, da indehaver <strong>Henrik Frederiksen</strong> fra Vesthimmerlands Materieludlejning gav hånd på en aftale om køb af tre nye Jekko minikraner.
              </p>

              {/* Ordre-liste uden tung boks-omkramning */}
              <div className="py-4 border-y border-gray-100 space-y-4">
                <h3 className="text-xs font-black uppercase tracking-[0.2em] text-gray-900 flex items-center gap-2">
                  <Zap className="h-4 w-4 text-brand-yellow" />
                  <span>Ordren består af:</span>
                </h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3 text-sm sm:text-base text-gray-800">
                    <CheckCircle2 className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-gray-900">2 stk. Jekko SPX532.2</strong> – Jekkos nyeste fuldelektriske minikran
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm sm:text-base text-gray-800">
                    <CheckCircle2 className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-gray-900">1 stk. Jekko SPX650</strong> – en stærk 5-tons elektrisk minikran
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm sm:text-base text-gray-800">
                    <CheckCircle2 className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-gray-900">1 stk. AlmaCrawler Multi-Loader 1.5 FX</strong> – 1,5-tons fuld elektrisk transport enhed
                    </div>
                  </li>
                  <li className="flex items-start gap-3 text-sm sm:text-base text-gray-800">
                    <CheckCircle2 className="h-5 w-5 text-brand-yellow shrink-0 mt-0.5" />
                    <div>
                      <strong className="font-bold text-gray-900">Ekstra tilbehør</strong> i form af håndterings- og løfteværktøjer til rør og bjælker, samt vakuumsug til ruder
                    </div>
                  </li>
                </ul>
              </div>

              {/* Anvendelsesområder & Beskrivelse */}
              <div className="space-y-4 text-sm sm:text-base text-gray-700">
                <p>
                  Minikranerne skal indgå i udlejningsflåden, hvor de bliver tilgængelige for blandt andet tømrere, smede og andre håndværkere med behov for fleksible og effektive løfteløsninger. Læs mere om mulighederne på vores dedikerede side om <Link to="/minikran" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minikran</Link>, eller udforsk <Link to="/liftudlejning" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">liftudlejning</Link> til personløft og montage i højden.
                </p>

                <p>
                  Uanset om opgaven er montering af spær, isætning af vinduer eller håndtering af I-profiler og rør, gør både SPX532.2 og SPX650 arbejdet lettere, mere effektivt og ikke mindst mere sikkert.
                  Med et bredt udvalg af værktøjer kan kranerne tilpasses mange forskellige opgaver, og det kompakte design gør dem ideelle til arbejde i baggårde, på byggepladser og i smalle passager. Skal du også have flyttet jord eller materialer på pladsen, tilbyder vi fleksibel leje af <Link to="/minilaesser" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minilæsser</Link>.
                </p>
              </div>

              {/* Investering & Unikke muligheder */}
              <div className="space-y-3 pt-2 text-sm sm:text-base text-gray-700 border-t border-gray-100">
                <p>
                  For os er investeringen et naturligt skridt i en fortsat satsning på minikraner og specialudstyr til bygge- og montagebranchen.
                </p>
                <p>
                  Det, der gør Jekko unik, er de mange muligheder for at tilpasse kranen med specialudstyr. Med blandt andet glasløfter, jib, grab, manipulator. Med de forskellige Pick & Carry-værktøjer kan du skræddersy løsningen til netop dine arbejdsopgaver.
                </p>
              </div>

              {/* Handlingsknapper */}
              <div className="pt-4 flex flex-wrap items-center gap-3 sm:gap-4">
                <Link
                  to="/minikran"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-yellow px-5 sm:px-6 py-3.5 text-xs font-black uppercase tracking-wider sm:tracking-widest text-white hover:bg-brand-yellow-hover transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer text-center"
                >
                  <span>Læs mere om minikran</span>
                  <ArrowRight className="h-4 w-4 shrink-0" />
                </Link>
                <a
                  href="https://vesthimmerlands.brukerweb.no/product/index/?productGroupId=306"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-5 sm:px-6 py-3.5 text-xs font-black uppercase tracking-wider sm:tracking-widest text-gray-800 hover:bg-gray-50 hover:border-brand-yellow transition-all duration-200 shadow-sm text-center"
                >
                  <span>Se kraner i produktkataloget</span>
                  <ExternalLink className="h-4 w-4 shrink-0" />
                </a>
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-5 sm:px-6 py-3.5 text-xs font-black uppercase tracking-wider sm:tracking-widest text-gray-700 hover:bg-gray-100 hover:border-brand-yellow transition-all duration-200 shadow-sm text-center"
                >
                  <span>Kontakt os om minikraner</span>
                  <ArrowRight className="h-4 w-4 text-brand-yellow shrink-0" />
                </Link>
              </div>

            </div>

          </div>

        </div>
      </section>

      {/* Sektion: Om os / Præsentation */}
      <section className="relative bg-white border-t border-brand-grey overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 py-24 md:px-6 md:py-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            {/* Venstre side: Billede */}
            <div className="lg:col-span-6">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl aspect-[4/3] sm:aspect-[16/10] lg:aspect-[4/3] border border-gray-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/vesthimmerlands-materieludlejning-maskinpark-svenstrup.jpg"
                  alt="Vesthimmerlands Materieludlejning maskinpark med lifte, minikraner og entreprenørmaskiner i Svenstrup"
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
              </div>
            </div>

            {/* Højre side: Tekst og information */}
            <div className="lg:col-span-6 flex flex-col justify-center items-start text-left lg:pl-6">
              <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                Vores Historie & Værdier
              </span>
              <h2 className="mt-3 text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl lg:text-5xl leading-tight">
                Pålidelig <span className="text-brand-yellow">Materieludlejning</span> til Professionelle
              </h2>
              <p className="mt-6 text-base text-gray-600 leading-relaxed">
                Hos Vesthimmerlands Materieludlejning A/S leverer vi moderne, driftssikkert og topklargjort udstyr til både store og småbyggerier. Vi forstår, at tid er penge på byggepladsen, og derfor går vi aldrig på kompromis med kvaliteten af vores maskiner eller vores service.
              </p>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                Med udgangspunkt i vores base i Svenstrup J dækker vi hele regionen med hurtig levering, professionel rådgivning og fleksible lejeaftaler, der er skræddersyet til dine behov. Uanset om du skal bruge professionel <Link to="/liftudlejning" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">liftudlejning</Link>, præcise løft med en <Link to="/minikran" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minikran</Link> eller terrængående <Link to="/minilaesser" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minilæsser</Link>, står vi klar til at hjælpe dig sikkert i mål.
              </p>
              
              <div className="mt-8">
                <Link
                  to="/om-os"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-yellow px-6 py-3.5 text-sm font-black uppercase tracking-widest text-white transition-all duration-200 hover:bg-brand-yellow-hover shadow-md hover:shadow-lg"
                >
                  <span>Læs mere</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion: Lokal materieludlejning i Vesthimmerland */}
      <section className="relative bg-brand-light py-24 md:py-32 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12 lg:items-stretch">
            
            {/* Venstre side: Tekst og information */}
            <div className="lg:col-span-6 flex flex-col justify-center items-start text-left">
              <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
                Lokal Forankring & Landsdækkende Service
              </span>
              <h2 className="mt-3 text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl lg:text-5xl leading-none">
                Lej hos os – <br />
                <span className="text-brand-yellow">god service til god pris.</span>
              </h2>
              
              <div className="mt-8 space-y-6 text-gray-600 leading-relaxed text-sm">
                <p>
                  Hos Vesthimmerlands Materieludlejning har vi alt det grej som du skal bruge til dit næste større eller mindre projekt.
                </p>
                <p>
                  Vi kan servicere de fleste erhverv, lige fra byggeri, montage, landbrug.
                </p>
                <p>
                  Vedligehold og funktions dygtighed er vores ansvar. Vi passer vores arbejde – så du kan passe dit.
                </p>
                <div className="border-l-2 border-brand-yellow pl-4 font-bold text-gray-800 italic bg-white p-4 rounded-r-lg border-y border-r border-gray-100 shadow-sm">
                  Vi indgår i et stærkt netværk af udlejnings virksomheder over hele landet, og vi kan servicere vores kunder uanset hvor de har brug for grej.
                </div>
              </div>

              <div className="mt-10">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 rounded-md bg-brand-yellow px-8 py-4 text-xs font-black uppercase tracking-[0.2em] text-white hover:bg-brand-yellow-hover transition-all duration-300 shadow-lg shadow-brand-yellow/10"
                >
                  <span>Kontakt os i dag</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Højre side: Billede */}
            <div className="lg:col-span-6 flex flex-col">
              <div className="relative overflow-hidden rounded-xl shadow-2xl h-full min-h-[400px] lg:min-h-0 border border-gray-200">
                <img
                  src={getOptimizedUrl("https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/materieludlejning-afdeling-jellingvej-svenstrup.jpg", 1000)}
                  alt="Vesthimmerlands Materieludlejning afdeling på Jellingvej 19 i Svenstrup J"
                  className="h-full w-full object-cover filter brightness-95 transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion: Video */}
      <section className="relative bg-white px-4 py-24 md:px-6 md:py-32 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl flex flex-col items-center text-center">
          <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow mb-4">
            Præsentation
          </span>
          <h2 className="text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl md:text-5xl max-w-3xl leading-tight">
            Se vores materiel i aktion
          </h2>
          <p className="mt-6 text-base text-gray-600 max-w-2xl leading-relaxed">
            Vi leverer professionelle maskiner, lifte og entreprenørmateriel til både store og små opgaver. Se en kort præsentation af vores brede udvalg og hør, hvorfor vi er din foretrukne lejekammerat.
          </p>

          {/* Video Container */}
          <div className="mt-16 w-full max-w-5xl aspect-video relative group cursor-pointer overflow-hidden rounded-xl shadow-2xl bg-black border border-gray-200">
            <video
              ref={videoRef}
              src="https://assets.auxodev.dk/assets/8fa00359-98ab-4f1d-89d3-09faf1928139/vesthimmerlands-materieludlejning-praesentation.mp4"
              className="w-full h-full object-cover"
              loop
              muted
              playsInline
              title="Præsentationsvideo af Vesthimmerlands Materieludlejning maskiner og lifte"
              aria-label="Videopræsentation af Vesthimmerlands Materieludlejning"
              onClick={togglePlay}
            />
            
            {/* Play Button Overlay */}
            {!isPlaying && (
              <div 
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/30 transition-all duration-300 group-hover:bg-black/40"
              >
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-brand-yellow text-white shadow-xl transition-all duration-300 hover:scale-110 hover:bg-brand-yellow-hover">
                  <Play className="h-10 w-10 fill-current ml-1" />
                </div>
              </div>
            )}

            {/* Pause Button Overlay on Hover when playing */}
            {isPlaying && (
              <div 
                onClick={togglePlay}
                className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/20 transition-all duration-300 opacity-0 hover:opacity-100"
              >
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-black/60 text-white shadow-xl transition-all duration-300 hover:scale-110">
                  <Pause className="h-10 w-10 fill-current" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sektion: Tillid, service og bredt udvalg */}
      <section className="relative bg-brand-light px-4 py-24 md:px-6 md:py-32 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            
            {/* Søjle 1: Tillid & Sikkerhed */}
            <div className="flex flex-col items-start text-left p-8 border border-brand-grey bg-white rounded-xl hover:border-brand-yellow/30 hover:shadow-xl transition-all duration-300">
              <div className="rounded-lg bg-brand-yellow/10 p-4 text-brand-yellow mb-6">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-black text-gray-900 uppercase tracking-wider">
                Driftssikkert materiel
              </h3>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                Kvaliteten og standen af vores materiel har altid været en mærkesag. Vi sikrer, at alt grej is godkendt, serviceret og klar til brug.
              </p>
            </div>

            {/* Søjle 2: Service & Levering */}
            <div className="flex flex-col items-start text-left p-8 border border-brand-grey bg-white rounded-xl hover:border-brand-yellow/30 hover:shadow-xl transition-all duration-300">
              <div className="rounded-lg bg-brand-yellow/10 p-4 text-brand-yellow mb-6">
                <Truck className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-black text-gray-900 uppercase tracking-wider">
                Hurtig levering
              </h3>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                Vi leverer hurtigt og præcist direkte til din byggeplads eller adresse i hele Vesthimmerland og omegn til fornuftige priser.
              </p>
            </div>

            {/* Søjle 3: Vedligeholdelse */}
            <div className="flex flex-col items-start text-left p-8 border border-brand-grey bg-white rounded-xl hover:border-brand-yellow/30 hover:shadow-xl transition-all duration-300">
              <div className="rounded-lg bg-brand-yellow/10 p-4 text-brand-yellow mb-6">
                <Wrench className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-black text-gray-900 uppercase tracking-wider">
                Professionel service
              </h3>
              <p className="mt-3 text-sm text-gray-600 leading-relaxed">
                Vores dygtige mekanikere og klargøringsfolk sørger for, at alt fungerer fejlfrit. Vi passer vores arbejde – så du kan passe dit.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Sektion: SEO - Materieludlejning, der passer til din opgave */}
      <section className="relative bg-white py-20 sm:py-24 md:py-32 border-t border-brand-grey overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          
          {/* Header & Intro */}
          <div className="max-w-4xl text-left mb-12 sm:mb-16">
            <span className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-[0.25em] text-brand-yellow mb-3">
              <span className="inline-block h-2 w-2 rounded-full bg-brand-yellow"></span>
              Materieloversigt & Vejledning
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tight text-gray-900 leading-tight">
              Materieludlejning, der passer til <span className="text-brand-yellow">din opgave</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-gray-700 leading-relaxed font-normal">
              Hos Vesthimmerlands Materieludlejning finder du maskiner, lifte og byggepladsudstyr til byggeri, montage og anlægsarbejde. Vi hjælper dig med at vælge materiel ud fra opgaven, så du ikke behøver at gætte dig frem til den løsning, der passer til arbejdet.
            </p>
          </div>

          {/* 4 Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            
            {/* Card 1: Find det materiel, opgaven kræver */}
            <div className="flex flex-col justify-between p-8 sm:p-10 rounded-2xl bg-gray-50 border border-gray-200/80 hover:border-brand-yellow/40 hover:bg-white hover:shadow-xl transition-all duration-300">
              <div>
                <div className="h-12 w-12 rounded-xl bg-brand-yellow/10 text-brand-yellow flex items-center justify-center mb-6">
                  <Layers className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Find det materiel, opgaven kræver
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                  <p>
                    Skal du grave, flytte jord eller gøre klar til et byggeri? Vores udvalg spænder fra gravemaskiner, minigravere og læssemaskiner til dumpere og komprimeringsmateriel. Vi tilbyder også udlejning af materiel til byggepladsen, herunder køreplader, stilladser, containere og trailere.
                  </p>
                  <p>
                    Når opgaven kræver strøm, lys eller et bedre arbejdsklima, kan du også leje generatorer, lystårne, pumper, affugtere samt udstyr til varme og ventilation. Det gør det lettere at samle de nødvendige maskiner og det praktiske udstyr hos én udlejer og få overblik over behovet.
                  </p>
                </div>
              </div>
            </div>

            {/* Card 2: Maskinudlejning til jordarbejde og anlæg */}
            <div className="flex flex-col justify-between p-8 sm:p-10 rounded-2xl bg-gray-50 border border-gray-200/80 hover:border-brand-yellow/40 hover:bg-white hover:shadow-xl transition-all duration-300">
              <div>
                <div className="h-12 w-12 rounded-xl bg-brand-yellow/10 text-brand-yellow flex items-center justify-center mb-6">
                  <Truck className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Maskinudlejning til jordarbejde og anlæg
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                  <p>
                    Vores maskinudlejning omfatter blandt andet minigravere, gravemaskiner, dumpere og læssemaskiner. Når pladsen er begrænset, kan en minigraver være relevant, mens en <Link to="/minilaesser" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minilæsser</Link> kan bruges til at flytte materialer effektivt på terrænet. Vi hjælper dig med at vælge ud fra opgaven og forholdene på stedet.
                  </p>
                </div>
              </div>
              <div className="mt-6 pt-4 border-t border-gray-200">
                <Link
                  to="/minilaesser"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover transition-colors"
                >
                  <span>Læs mere om leje af minilæsser</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* Card 3: Lifte og kraner til arbejde i højden */}
            <div className="flex flex-col justify-between p-8 sm:p-10 rounded-2xl bg-gray-50 border border-gray-200/80 hover:border-brand-yellow/40 hover:bg-white hover:shadow-xl transition-all duration-300">
              <div>
                <div className="h-12 w-12 rounded-xl bg-brand-yellow/10 text-brand-yellow flex items-center justify-center mb-6">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Lifte og kraner til arbejde i højden
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                  <p>
                    Til montage, facadeopgaver og arbejde i højden tilbyder vi <Link to="/liftudlejning" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">liftudlejning</Link> med blandt andet bomlifte og sakselifte. Skal du løfte materialer på steder med begrænset plads, kan en <Link to="/minikran" className="font-semibold text-gray-900 underline decoration-brand-yellow/60 underline-offset-4 hover:text-brand-yellow hover:decoration-brand-yellow transition-colors">minikran</Link> være den helt rigtige løsning. Valget afhænger blandt andet af adgangsforholdene og det arbejde, du skal udføre.
                  </p>
                  <p>
                    Vi hjælper dig gerne med at afklare, hvilken lift eller kran der passer til arbejdet. Vores egne mekanikere vedligeholder løbende maskinerne, og vores lifte er godkendte og klar til brug. Har du brug for flere typer udstyr til samme opgave, ser vi på behovet samlet.
                  </p>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3">
                <Link
                  to="/liftudlejning"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                >
                  <span>Læs om liftudlejning</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/minikran"
                  className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-yellow hover:text-brand-yellow-hover"
                >
                  <span>Læs om minikran</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>

            {/* Card 4: Udlejning af materiel med rådgivning fra start */}
            <div className="flex flex-col justify-between p-8 sm:p-10 rounded-2xl bg-gray-50 border border-gray-200/80 hover:border-brand-yellow/40 hover:bg-white hover:shadow-xl transition-all duration-300">
              <div>
                <div className="h-12 w-12 rounded-xl bg-brand-yellow/10 text-brand-yellow flex items-center justify-center mb-6">
                  <ShieldCheck className="h-6 w-6" />
                </div>
                <h3 className="text-lg sm:text-xl font-black uppercase tracking-tight text-gray-900 mb-4">
                  Udlejning af materiel med rådgivning fra start
                </h3>
                <div className="space-y-4 text-sm sm:text-base text-gray-600 leading-relaxed">
                  <p>
                    God materieludlejning handler om mere end at få en maskine til rådighed. Fortæl os om opgaven, pladsen på stedet og den forventede lejeperiode, så rådgiver vi om relevante muligheder. Vi hjælper kunder inden for byggeri, entreprenørarbejde, montage og landbrug og tilbyder fleksible lejeaftaler.
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Fremhævet Lokationsboks: Materieludlejning fra Svenstrup og Løgstør */}
          <div className="mt-10 sm:mt-12 rounded-2xl bg-gradient-to-br from-gray-50 via-white to-gray-50 border border-gray-200 p-8 sm:p-12 shadow-sm hover:border-brand-yellow/30 transition-all duration-300">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:items-center">
              <div className="lg:col-span-8 space-y-4 text-left">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-brand-yellow/10 text-brand-yellow border border-brand-yellow/20">
                    <MapPin className="h-3.5 w-3.5" />
                    Svenstrup (ved Aalborg)
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-brand-yellow/10 text-brand-yellow border border-brand-yellow/20">
                    <MapPin className="h-3.5 w-3.5" />
                    Løgstør
                  </span>
                  <span className="text-xs text-gray-400 font-semibold">• Aalborg, Nordjylland & Aarhus</span>
                </div>

                <h3 className="text-xl sm:text-2xl lg:text-3xl font-black uppercase tracking-tight text-gray-900">
                  Materieludlejning fra Svenstrup og Løgstør
                </h3>
                
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Du finder os i Svenstrup ved Aalborg og i Løgstør. Herfra hjælper vi kunder med projekter i Aalborg, resten af Nordjylland og Aarhus. Har du brug for materieludlejning i Aalborg eller materiel til en opgave et andet sted, tager vi gerne en snak om dit behov.
                </p>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                  Fortæl os, hvad du skal lave, hvor arbejdet foregår, og hvornår du skal bruge udstyret. Så kan vi sammen finde frem til relevant materiel og en lejeaftale, der passer til arbejdet. Kontakt os for rådgivning og et uforpligtende tilbud, før du beslutter dig.
                </p>
              </div>

              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-yellow px-6 py-4 text-xs font-black uppercase tracking-widest text-white hover:bg-brand-yellow-hover transition-all duration-200 shadow-md hover:shadow-lg text-center"
                >
                  <span>Få et uforpligtende tilbud</span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={`tel:${COMPANY_INFO.phoneRaw}`}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-6 py-4 text-xs font-black uppercase tracking-widest text-gray-800 hover:bg-gray-50 hover:border-brand-yellow transition-all duration-200 shadow-sm text-center"
                >
                  <Phone className="h-4 w-4 text-brand-yellow" />
                  <span>Ring: {COMPANY_INFO.phone}</span>
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Sektion: Kundeanmeldelser */}
      <section className="relative bg-white py-24 md:py-32 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center mb-16">
            <span className="text-xs font-black uppercase tracking-[0.25em] text-brand-yellow">
              Kundeudtalelser
            </span>
            <h2 className="mt-2 text-3xl font-black uppercase tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Hvad siger <span className="text-brand-yellow">vores kunder?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {TESTIMONIALS.map((t, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-between p-8 border border-brand-grey bg-brand-light rounded-xl hover:border-brand-yellow/30 hover:bg-white hover:shadow-xl transition-all duration-300"
              >
                <div>
                  {/* Stjerner */}
                  <div className="flex items-center gap-1 text-brand-yellow mb-6">
                    {[...Array(t.stars)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 italic leading-relaxed">
                    {t.text}
                  </p>
                </div>
                <div className="mt-8 border-t border-gray-200 pt-6">
                  <span className="block text-sm font-black text-gray-900 uppercase tracking-wider">
                    {t.author}
                  </span>
                  <span className="text-xs text-gray-500 uppercase tracking-widest mt-1 block">Kunde</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sektion: Vores Materieludlejere */}
      <section className="relative bg-brand-light px-4 py-24 md:px-6 border-t border-brand-grey">
        <div className="mx-auto max-w-7xl">
          <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest mb-12 text-center">
            Vores Materieludlejere
          </h3>
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 max-w-4xl mx-auto">
            {STAFF.map((member, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center text-center border border-brand-grey bg-white p-8 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                <img
                  src={getOptimizedUrl(member.image, 400)}
                  alt={`${member.name} - ${member.role} hos Vesthimmerlands Materieludlejning`}
                  className="h-32 w-32 md:h-[160px] md:w-[160px] rounded-full object-cover border border-gray-200 mb-6 shadow-sm"
                  loading="lazy"
                />
                <div>
                  <h4 className="text-lg font-black text-gray-900 uppercase tracking-wider">
                    {member.name}
                  </h4>
                  <p className="text-xs text-gray-500 uppercase tracking-widest mt-1">{member.role}</p>
                  <div className="mt-4 space-y-1 text-sm">
                    <a href={`mailto:${member.email}`} className="block text-brand-yellow hover:underline font-semibold">
                      {member.email}
                    </a>
                    <a href={`tel:${member.phoneRaw}`} className="block text-gray-600 hover:text-brand-yellow font-medium">
                      {member.phone}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
